import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import Layout from '../../components/Layout';
import { Card } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { Checkbox } from '../../components/ui/checkbox';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../../components/ui/table';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '../../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { ClipboardList, Plus, Wrench } from 'lucide-react';
import { useAuth } from '../../context/AuthContext';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const TYPE_LABEL = { M1: 'M1 · Malfunction', M2: 'M2 · Maint. Request', M3: 'M3 · Activity' };
const statusColor = (s) => ({
  Outstanding: 'bg-red-100 text-red-700', 'In Process': 'bg-amber-100 text-amber-700',
  Completed: 'bg-green-100 text-green-700', Postponed: 'bg-gray-200 text-gray-600',
}[s] || 'bg-gray-100');
const prioColor = (p) => ({ 'Very High': 'text-red-700', High: 'text-red-600', Medium: 'text-amber-600', Low: 'text-gray-500' }[p] || '');
const EMPTY = { notification_type: 'M1', description: '', equipment: '', functional_location: '', priority: 'Medium', reported_by: '', breakdown: false };

const Notifications = () => {
  const { canWrite } = useAuth();
  const navigate = useNavigate();
  const writable = canWrite('maintenance_notification');
  const orderWritable = canWrite('maintenance_order');
  const [rows, setRows] = useState([]);
  const [equipment, setEquipment] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(EMPTY);
  const [saving, setSaving] = useState(false);
  const [detail, setDetail] = useState(null);
  const [orderDesc, setOrderDesc] = useState('');
  const [orderType, setOrderType] = useState('Corrective');
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try { const res = await axios.get(`${API}/pm/notifications`, { withCredentials: true }); setRows(res.data); }
    catch (e) { toast.error('Failed to load notifications'); }
    finally { setLoading(false); }
  }, []);
  const loadEquipment = useCallback(async () => {
    try { const res = await axios.get(`${API}/pm/equipment`, { withCredentials: true }); setEquipment(res.data); }
    catch (e) { /* non-blocking */ }
  }, []);

  useEffect(() => { load(); loadEquipment(); }, [load, loadEquipment]);

  const set = (k, v) => setForm((p) => ({ ...p, [k]: v }));

  const create = async () => {
    setSaving(true);
    try {
      await axios.post(`${API}/pm/notifications`, {
        ...form, equipment: form.equipment || null, functional_location: form.functional_location || null,
      }, { withCredentials: true });
      toast.success('Notification created'); setOpen(false); setForm(EMPTY); load();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed to create'); }
    finally { setSaving(false); }
  };

  const updateStatus = async (number, status) => {
    try {
      const res = await axios.put(`${API}/pm/notifications/${number}`, { status }, { withCredentials: true });
      setDetail(res.data); toast.success('Status updated'); load();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed to update'); }
  };

  const openDetail = (r) => { setDetail(r); setOrderDesc(`Repair: ${r.description}`); setOrderType(r.breakdown ? 'Breakdown' : 'Corrective'); };

  const createOrder = async () => {
    setBusy(true);
    try {
      const res = await axios.post(`${API}/pm/notifications/${detail.notification_number}/create-order`, {
        order_type: orderType, description: orderDesc || `Order for ${detail.notification_number}`,
      }, { withCredentials: true });
      toast.success(`Work order ${res.data.order_number} created`);
      setDetail(null); load();
      navigate('/maintenance/orders');
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed to create order'); }
    finally { setBusy(false); }
  };

  return (
    <Layout>
      <div className="p-6 space-y-6" data-testid="pm-notifications-page">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="p-2 rounded-lg bg-sky-100 text-sky-700"><ClipboardList className="h-5 w-5" /></div>
            <div>
              <h1 className="text-2xl font-bold text-gray-900">Maintenance Notifications</h1>
              <p className="text-gray-600 text-sm mt-0.5">Malfunctions, maintenance requests and activity reports.</p>
            </div>
          </div>
          {writable && <Button onClick={() => { setForm(EMPTY); setOpen(true); }} data-testid="new-notification-btn"><Plus className="h-4 w-4 mr-2" />New Notification</Button>}
        </div>

        <Card className="p-0 overflow-hidden">
          <Table>
            <TableHeader><TableRow>
              <TableHead>Number</TableHead><TableHead>Type</TableHead><TableHead>Description</TableHead>
              <TableHead>Equipment</TableHead><TableHead>Priority</TableHead><TableHead>Breakdown</TableHead>
              <TableHead>Status</TableHead><TableHead>Order</TableHead><TableHead></TableHead>
            </TableRow></TableHeader>
            <TableBody>
              {loading ? <TableRow><TableCell colSpan={9} className="text-center py-8 text-gray-500">Loading...</TableCell></TableRow>
                : rows.length === 0 ? <TableRow><TableCell colSpan={9} className="text-center py-8 text-gray-500">No notifications yet.</TableCell></TableRow>
                : rows.map((r) => (
                  <TableRow key={r.notification_number} data-testid={`notif-row-${r.notification_number}`}>
                    <TableCell className="font-medium">{r.notification_number}</TableCell>
                    <TableCell>{TYPE_LABEL[r.notification_type] || r.notification_type}</TableCell>
                    <TableCell className="max-w-xs truncate">{r.description}</TableCell>
                    <TableCell>{r.equipment || '—'}</TableCell>
                    <TableCell className={`font-medium ${prioColor(r.priority)}`}>{r.priority}</TableCell>
                    <TableCell>{r.breakdown ? <span className="text-red-600 font-medium">Yes</span> : 'No'}</TableCell>
                    <TableCell><span className={`px-2 py-1 rounded text-xs font-medium ${statusColor(r.status)}`}>{r.status}</span></TableCell>
                    <TableCell>{r.work_order || '—'}</TableCell>
                    <TableCell><Button size="sm" variant="outline" onClick={() => openDetail(r)} data-testid={`open-notif-${r.notification_number}`}>Open</Button></TableCell>
                  </TableRow>
                ))}
            </TableBody>
          </Table>
        </Card>
      </div>

      {/* Create */}
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>New Maintenance Notification</DialogTitle>
            <DialogDescription>Report a malfunction or raise a maintenance request.</DialogDescription>
          </DialogHeader>
          <div className="grid grid-cols-2 gap-4 py-2">
            <div>
              <Label>Type</Label>
              <Select value={form.notification_type} onValueChange={(v) => set('notification_type', v)}>
                <SelectTrigger data-testid="notif-type"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="M1">M1 · Malfunction Report</SelectItem>
                  <SelectItem value="M2">M2 · Maintenance Request</SelectItem>
                  <SelectItem value="M3">M3 · Activity Report</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Priority</Label>
              <Select value={form.priority} onValueChange={(v) => set('priority', v)}>
                <SelectTrigger data-testid="notif-priority"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="Very High">Very High</SelectItem>
                  <SelectItem value="High">High</SelectItem>
                  <SelectItem value="Medium">Medium</SelectItem>
                  <SelectItem value="Low">Low</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="col-span-2"><Label>Description *</Label><Textarea data-testid="notif-description" rows={2} value={form.description} onChange={(e) => set('description', e.target.value)} /></div>
            <div>
              <Label>Equipment</Label>
              <Select value={form.equipment || 'none'} onValueChange={(v) => set('equipment', v === 'none' ? '' : v)}>
                <SelectTrigger data-testid="notif-equipment"><SelectValue placeholder="None" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">None</SelectItem>
                  {equipment.map((eq) => <SelectItem key={eq.equipment_number} value={eq.equipment_number}>{eq.equipment_number} — {eq.description}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div><Label>Reported By</Label><Input data-testid="notif-reportedby" value={form.reported_by} onChange={(e) => set('reported_by', e.target.value)} /></div>
            <div className="col-span-2 flex items-center gap-2 rounded-lg border p-3">
              <Checkbox id="breakdown" data-testid="notif-breakdown" checked={form.breakdown} onCheckedChange={(v) => set('breakdown', !!v)} />
              <Label htmlFor="breakdown" className="cursor-pointer">Breakdown (equipment is down)</Label>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button>
            <Button onClick={create} disabled={saving || !form.description} data-testid="save-notification-btn">{saving ? 'Saving...' : 'Create'}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Detail */}
      <Dialog open={!!detail} onOpenChange={(o) => !o && setDetail(null)}>
        <DialogContent className="max-w-lg">
          {detail && (
            <>
              <DialogHeader>
                <DialogTitle className="flex items-center gap-3">{detail.notification_number}
                  <span className={`px-2 py-1 rounded text-xs font-medium ${statusColor(detail.status)}`} data-testid="notif-detail-status">{detail.status}</span>
                </DialogTitle>
                <DialogDescription>{TYPE_LABEL[detail.notification_type]} · {detail.priority} priority</DialogDescription>
              </DialogHeader>
              <div className="space-y-2 text-sm">
                <div><span className="text-gray-500">Equipment:</span> {detail.equipment || '—'}</div>
                <div><span className="text-gray-500">Breakdown:</span> {detail.breakdown ? 'Yes' : 'No'}</div>
                <div className="pt-1"><span className="text-gray-500">Description:</span><p className="mt-1">{detail.description}</p></div>
                {detail.work_order && <div className="text-sky-700 font-medium">Linked Work Order: {detail.work_order}</div>}
              </div>

              {orderWritable && !detail.work_order && (
                <Card className="p-4 border-sky-100 space-y-3">
                  <h4 className="font-semibold text-gray-900 flex items-center gap-2"><Wrench className="h-4 w-4 text-sky-600" />Create Work Order</h4>
                  <div className="grid grid-cols-3 gap-3">
                    <div className="col-span-2"><Label>Order Description</Label><Input data-testid="order-desc" value={orderDesc} onChange={(e) => setOrderDesc(e.target.value)} /></div>
                    <div>
                      <Label>Order Type</Label>
                      <Select value={orderType} onValueChange={setOrderType}>
                        <SelectTrigger data-testid="order-type"><SelectValue /></SelectTrigger>
                        <SelectContent>
                          <SelectItem value="Breakdown">Breakdown</SelectItem>
                          <SelectItem value="Corrective">Corrective</SelectItem>
                          <SelectItem value="Calibration">Calibration</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                  </div>
                  <Button onClick={createOrder} disabled={busy} data-testid="create-order-from-notif-btn">Create Work Order</Button>
                </Card>
              )}

              {writable && (
                <DialogFooter className="gap-2">
                  {detail.status !== 'In Process' && <Button variant="outline" size="sm" onClick={() => updateStatus(detail.notification_number, 'In Process')} data-testid="notif-inprocess-btn">Mark In Process</Button>}
                  {detail.status !== 'Completed' && <Button size="sm" onClick={() => updateStatus(detail.notification_number, 'Completed')} data-testid="notif-complete-btn">Complete</Button>}
                </DialogFooter>
              )}
            </>
          )}
        </DialogContent>
      </Dialog>
    </Layout>
  );
};

export default Notifications;
